<template>
  <AutoDialogDrawer placement="centre" modal :dismissible="true" @close="$emit('close')">
    <template #title>{{ translate('Start Domain Verification') }}</template>

    <p>
      {{
        translate(
          "Enter a domain and an email address belonging to that domain. We'll send a code to this address to complete the domain verification process.",
        )
      }}
    </p>

    <BootstrapFormControl
      v-model="domain"
      class="field"
      :name="translate('Domain')"
      :server-error="!!domainError"
      :error="domainError"
      :disabled="isSubmitting"
      required
      @enter="submitVerificationRequest"
    />
    <BootstrapFormControl
      v-model="email"
      class="field"
      :name="translate('Verification Code Email')"
      :server-error="!!emailError"
      :error="emailError"
      :disabled="isSubmitting"
      required
      @enter="submitVerificationRequest"
    />

    <template v-if="otherErrors.length > 0">
      <h5>Errors:</h5>
      <p v-for="(error, index) in otherErrors" :key="index" class="text-danger">{{ error }}</p>
    </template>

    <template #buttons>
      <BootstrapButton name="cancel" variant="secondary" class="me-2" @click="$emit('close')">
        {{ translate('Cancel') }}
      </BootstrapButton>
      <BootstrapButton
        name="submit"
        variant="primary"
        :aria-label="translate('Start Domain Verification')"
        :disabled="isSubmitting"
        @click="submitVerificationRequest"
      >
        {{ translate('Start') }}
      </BootstrapButton>
    </template>
  </AutoDialogDrawer>
</template>
<script setup lang="ts">
import {useLocalisation} from '@/composables/useLocalisation';
import {errorResponseToErrorMap} from '@/sphere/error';

const emit = defineEmits<{close: []}>();

const loginPolicy = useLoginPolicyStore();
const {translate} = useLocalisation();

const domain = ref('');
const email = ref('');
const errors = ref<{[index: string]: string[]}>({});

const isSubmitting = ref(false);

const domainError = computed(() =>
  errors.value.domain ? errors.value.domain.join(', ') : undefined,
);
const emailError = computed(() =>
  errors.value.sent_to ? errors.value.sent_to.join(', ') : undefined,
);
const otherErrors = computed(() => {
  const errorEntries = Object.entries(errors.value).filter(
    ([key, _]) => !['domain', 'sent_to'].includes(key),
  );
  errorEntries.sort(([key1, _1], [key2, _2]) => key1.localeCompare(key2));
  return errorEntries.flatMap(([_, value]) => value);
});

const cleanDomain = (domain: string): string => {
  // Someone might enter a URL so we just extract the domain
  domain = domain.trim();
  const match = domain.match(/https?:\/\/([^/]*)/);
  if (match) {
    return match[1];
  } else {
    return domain;
  }
};

const submitVerificationRequest = async () => {
  errors.value = {};
  domain.value = cleanDomain(domain.value);
  email.value = email.value.trim();

  try {
    isSubmitting.value = true;
    await loginPolicy.startDomainVerification(domain.value, email.value);
    emit('close');
  } catch (error) {
    errors.value = errorResponseToErrorMap(error);
  } finally {
    isSubmitting.value = false;
  }
};
</script>
<style lang="scss" scoped>
.field {
  margin-bottom: 1rem;
}
</style>
